import { HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { plainToInstance } from "class-transformer";
import { has } from "lodash-es";
import { ActiveToast, ToastrService } from "ngx-toastr";
import { NEVER, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { TAPIListResult } from "../contract/api.contract";
import { HeaderTokenEnum } from "../contract/auth.contract";
import { ErrorMessage } from "../model/api/error.model";
import { IQueryFilter, QueryResult } from "../model/api/query-filter.model";
import { logger } from "./logger.util";

const className = "apiCallWrapper";

export const apiHost = environment.endpoint;
export const apiPrefix = "";

/**
 * @description Creates an application URL by concatenating the host, prefix, and additional path parameters.
 * @param {string} host - The host part of the URL.
 * @param {string} prefix - The prefix to be added before the path parameters.
 * @param {boolean} wsUrl - Whether the url is for websocket (true) or http (false). Default false
 * @returns {(params: Array<string | number>) => string} - A function that accepts an array of path parameters and returns the generated application URL.
 * @example
 * ```
 * const generateUrl = createAppUrl('https://example.com', 'api');
 *
 * const url1 = generateUrl('users'); // 'https://example.com/api/users'
 * const url2 = generateUrl('posts', 123); // 'https://example.com/api/posts/123'
 * ```
 */
export const createAppUrl =
  (host: string, prefix: string, wsUrl = false) =>
  (...params: Array<string | number>): string => {
    const appUrl = `${host}${prefix.length ? prefix + "/" : ""}/${params.join("/")}`;
    if (wsUrl) {
      return appUrl.replace(/https/gm, "wss").replace(/http/gm, "ws");
    } else {
      return appUrl;
    }
  };

/**
 * @description Creates a URL using the `createAppUrl` function with pre-defined parameters for the API host and prefix.
 * @returns {(params: Array<string | number>) => string} - A function that accepts an array of path parameters and returns the generated URL.
 * @example
 * ```
 * const generateUrl = createUrl;
 *
 * const url1 = generateUrl('users'); // 'https://example.com/api/users'
 * const url2 = generateUrl('posts', 123); // 'https://example.com/api/posts/123'
 * ```
 */
export const createUrl = createAppUrl(apiHost, apiPrefix);

/**
 * @description Creates a WebSocket URL using the `createAppUrl` function with pre-defined parameters for the API host and prefix.
 * @returns {(params: Array<string | number>) => string} - A function that accepts an array of path parameters and returns the generated URL.
 * @example
 * ```
 * const generateUrl = createUrl;
 *
 * const url1 = generateUrl('users'); // 'ws://example.com/api/users'
 * const url2 = generateUrl('posts', 123); // 'ws://example.com/api/posts/123'
 * ```
 */
export const createWSUrl = createAppUrl(apiHost, apiPrefix, true);

/**
 * @description Creates a new instance of `HttpParams` to build HTTP request parameters.
 * @returns {HttpParams} - A new instance of `HttpParams`.
 * @example
 * ```
 * const params = httpParams();
 *
 * params = params.append('param1', 'value1');
 * params = params.append('param2', 'value2');
 *
 * console.log(params.toString()); // 'param1=value1&param2=value2'
 * ```
 */
export const httpParams = () => new HttpParams();

/**
 * @description Converts a query object into an instance of `HttpParams` to be used in an HTTP request.
 * @param {IQueryFilter} query - The query object containing key-value pairs.
 * @returns {HttpParams} - An instance of `HttpParams` with the converted query parameters.
 * @example
 * ```
 * const query = {
 *   param1: 'value1',
 *   param2: { nestedParam: 'nestedValue' },
 *   param3: (param) => param > 10
 * };
 *
 * const params = queryToParams(query);
 *
 * console.log(params.toString());
 * // 'param1=value1&param2={"nestedParam":"nestedValue"}'
 * ```
 */
export const queryToParams = (query: IQueryFilter) =>
  Object.entries(query).reduce(
    (params, [key, value]) =>
      typeof value === "function"
        ? params
        : params.set(key, typeof value === "object" ? JSON.stringify(value) : value),
    httpParams(),
  );

/**
 * @description Retrieves the headers for public routes by creating a new instance of `HttpHeaders` and appending the 'tokenType' header with the value 'NoToken'.
 * @returns {HttpHeaders} - An instance of `HttpHeaders` with the headers for public routes.
 * @example
 * ```
 * const publicHeaders = getPublicRoutesHeaders();
 *
 * console.log(publicHeaders.get('tokenType')); // 'NoToken'
 * ```
 */

export const getPublicRoutesHeaders = () => {
  const headers = new HttpHeaders();

  return headers.append("tokenType", HeaderTokenEnum.NoToken);
};

let offlineNotification: ActiveToast<any> | null = null;

/**
 * @description Wraps an API call observable with error handling, notifications, and success notifications.
 * @param {Observable<N>} observable - The API call observable to be wrapped.
 * @param {Object} opts - Options for configuring the behavior of the wrapper.
 * @param {ToastrService} opts.notificationsService - The notifications service used for displaying notifications.
 * @param {string} opts.action - The action being performed by the API call.
 * @param {string} [opts.title] - The title for the notification displayed during the API call.
 * @param {string} [opts.message] - The message for the notification displayed during the API call.
 * @param {string} [opts.successTitle] - The title for the success notification displayed after a successful API call.
 * @param {string} [opts.failTitle] - The title for the fail notification displayed after a failed API call.
 * @param {string} [opts.successMessage] - The message for the success notification displayed after a successful API call.
 * @param {D} [opts.defaultValue] - The default value to return if the entity is not found, instead of an error (eg: null)
 * @returns {Observable<N>} - The wrapped API call observable.
 * @example
 * ```
 * const observable = apiCallWrapper(
 *   someApiCallObservable,
 *   {
 *     notificationsService: myNotificationsService,
 *     action: 'Get Data',
 *     title: 'Fetching data',
 *     message: 'Please wait...',
 *     successTitle: 'Data Retrieved',
 *     failTitle: 'Error retrieving data',
 *     successMessage: 'Data retrieval completed',
 *     defaultValue: []
 *   }
 * );
 *
 * observable.subscribe(
 *   data => console.log('API call success:', data),
 *   error => console.error('API call error:', error)
 * );
 * ```
 */
export const apiCallWrapper = <D, N>(
  observable: Observable<N>,
  opts: {
    notificationsService: ToastrService;
    action: string;
    title?: string;
    message?: string;
    successTitle?: string;
    failTitle?: string;
    successMessage?: string;
    failMessage?: string;
    defaultValue?: D;
    /** Defaults to false */
    showSuccessNotification?: boolean;
  },
): typeof observable => {
  const signature = className + ".apiCallWrapper: ";
  const options = Object.assign(
    {},
    {
      title: opts.action,
      message: "",
      successTitle: `${opts.action} complete`,
      failTitle: `${opts.action} failed`,
      successMessage: opts.message ? `${opts.message} completed` : "",
      failMessage: "",
      defaultValue: [],
      showSuccessNotification: false,
    },
    opts,
  );

  // Sent to true when there was a gracefully handled error and the default value was returned.
  let didNotSucceed = false;

  // Ensure no offline notifications are being displayed if an offline state is not detected
  if (offlineNotification && window.navigator.onLine) {
    options.notificationsService.remove(offlineNotification.toastId);
    offlineNotification = null;
  }

  if (!window.navigator.onLine) {
    // Set an offline notification if one doesn't already exist
    if (!offlineNotification || offlineNotification.onHidden) {
      offlineNotification = options.notificationsService.error(
        "No internet connection available.",
        options.failTitle,
        {
          timeOut: 10_000,
          progressBar: true,
        },
      );

      if (offlineNotification.onTap) {
        offlineNotification.onTap.subscribe(() => {
          options.notificationsService.remove(offlineNotification!.toastId);
          offlineNotification = null;
        });
      }
    }

    logger.silly(signature + `Ignoring API Request for offline connection`);

    const handledError = new ErrorMessage({
      message: "Browser is offline",
      handled: true,
    });
    throw handledError;
  }

  return observable.pipe(
    catchError((err) => {
      logger.silly(signature + "Handling Error");

      // Prevent duplicate handling of the error
      if (err instanceof ErrorMessage) {
        if (err.handled) {
          return NEVER;
        }

        throw err;
      }

      console.log(err);
      if (has(err, "error") && has(err.error, "message") && has(err.error, "statusCode")) {
        const error = new ErrorMessage().deserialize(err.error as Partial<ErrorMessage>);
        const message =
          options.failMessage ||
          (has(err.error, "detail") && has(err.error.detail, "message")
            ? err.error.detail.message
            : error.message);
        options.notificationsService.error(message, options.failTitle, {
          timeOut: 6_000,
          progressBar: true,
        });

        if (error.statusCode === 404 && opts.defaultValue) {
          logger.warn(signature + `Gracefully 404 Handled Error`);
          didNotSucceed = true;
          return of(options.defaultValue);
        }

        throw error;
      }

      if (err instanceof HttpErrorResponse && err.status === 0) {
        options.notificationsService.error("Error communicating with server", options.failTitle, {
          timeOut: 6_000,
          progressBar: true,
        });

        const handledError = new ErrorMessage({ handled: true });

        throw handledError;
      }

      options.notificationsService.error("Unknown Error has Occurred", options.failTitle, {
        timeOut: 6_000,
        progressBar: true,
      });

      throw new ErrorMessage();
    }),
    map((result) => {
      if (!didNotSucceed && options.showSuccessNotification) {
        options.notificationsService.success(options.successMessage, options.successTitle, {
          timeOut: 2_000,
          progressBar: true,
        });
      }

      return result as N;
    }),
  );
};

type SocketMessage<T> = {
  event: "message",
  data: T
}

type SocketError = {
  event: "error",
  data: unknown
}

type SocketKeepAlive = {
  event: "keepalive",
  data: 'ping' | 'pong'
};

export type SocketData<T = unknown> = SocketMessage<T> | SocketError | SocketKeepAlive;

// This differs from apiCallWrapper in the error handling.
export const apiSubscriptionWrapper = <N>(
  observable: Observable<SocketData<N>>,
  opts: {
    notificationsService: ToastrService;
    failTitle: string;
    failMessage?: string;
  },
): Observable<N> => {
  const signature = className + ".apiSubscriptionWrapper: ";

  return observable.pipe(
    catchError((error) => {
      // TODO not sure that's ever reached
      console.error(error);
      return error;
    }),
    map((result: SocketData) => {
      if (result.event === "error") {
        if (has(result.data, "statusCode") && result.data.statusCode === 404) {
          opts.notificationsService.error(opts.failMessage, "Not found", {
            timeOut: 6_000,
            progressBar: true,
          });
        } else {
          opts.notificationsService.error(opts.failMessage, opts.failTitle, {
            timeOut: 6_000,
            progressBar: true,
          });
        }
        throw new ErrorMessage();
      } else {
        return result.data as N;
      }
    }),
  );
};

/**
 * @description This method is used to inflate the model from the data returned from the API.
 * @param model The model to inflate
 * @param data The data to inflate the model with
 * @returns
 * example:
 * return this.inflateModel<Deal, Deal & HasIdOrExternalIdOrServiceName<string>>(Deal, data);
 */
export const inflateModel = async <T, K>(
  model: new () => T,
  data: QueryResult<K>,
): Promise<TAPIListResult<T>> => {
  try {
    const result: TAPIListResult<T> = { count: 0, rows: [] };
    const modelInstances: T[] = [];

    if (data && data.rows && data.rows.length > 0) {
      for (let i = 0; i < data.rows.length; i++) {
        modelInstances.push(plainToInstance(model, data.rows[i]));
      }

      result.count = data.count;
      result.rows = modelInstances;
    }

    return result;
  } catch (error) {
    throw error;
  }
};
